/**
 * Página /#mpls — overview + drill-down por device (read-only F6 MVP).
 *
 * - Overview: cards (total, mplsEnabledDevices, byType, byRole) + lista de devices
 *   com count de LSPs e lastSeen. Click → drill.
 * - Detalhe: tabela paginada de LSPs com filtros (lspType, role, fec search) e
 *   botão "↻ Re-sincronizar" (OPERATOR+) que dispara poll on-demand.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type User } from '../api';

type OverviewResp = {
  total: number;
  mplsEnabledDevices: number;
  byType: Record<string, number>;
  byRole: Record<string, number>;
  perDevice: Array<{
    deviceId: number;
    count: number;
    lastSeen: string | null;
    device: { id: number; hostname: string; mgmtIp: string; monitored: boolean; popId: number | null; vendor: { code: string } } | null;
  }>;
};

type LspsResp = {
  device: { id: number; hostname: string; mgmtIp: string; mplsEnabled: boolean; vendor: { code: string }; model: { code: string | null; driverClass: string } };
  total: number; take: number; skip: number;
  lastSeen: string | null;
  lsps: Array<{
    id: string; deviceId: number; lspType: string; fec: string; role: string;
    action: string | null; inLabel: number | null; outLabel: number | null;
    inInterface: string | null; outInterface: string | null; vrfName: string | null;
    bandwidthKbps: number | null; destination: string | null; status: string | null;
    lastSeen: string; source: string;
  }>;
};

function fmtAge(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const sec = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `há ${sec}s`;
  if (sec < 3600) return `há ${Math.round(sec / 60)}m`;
  if (sec < 86400) return `há ${Math.round(sec / 3600)}h`;
  return d.toLocaleString('pt-BR');
}

const TYPE_COLOR: Record<string, string> = {
  LDP: 'text-cyan-400',
  RSVP_TE: 'text-fuchsia-400',
  BGP_LSP: 'text-amber-400',
  BGP_IPV6_LSP: 'text-amber-300',
  STATIC: 'text-slate-400',
  EVPN: 'text-violet-400',
  UNKNOWN: 'text-slate-500'
};
const ROLE_COLOR: Record<string, string> = {
  INGRESS: 'text-emerald-400',
  TRANSIT: 'text-sky-400',
  EGRESS: 'text-rose-400',
  UNKNOWN: 'text-slate-500'
};

export function MplsPage({ user, subRoute }: { user: User; subRoute: string | null }) {
  const deviceId = subRoute ? Number(subRoute) : null;
  return deviceId ? <MplsDeviceDetail deviceId={deviceId} canWrite={user.role === 'OPERATOR' || user.role === 'ADMIN'} />
                  : <MplsOverview />;
}

function MplsOverview() {
  const q = useQuery<OverviewResp>({
    queryKey: ['mpls', 'overview'],
    queryFn: () => api.get('/api/mpls/overview'),
    refetchInterval: 60_000
  });

  if (q.isLoading) return <div className="p-6 text-slate-400">carregando overview…</div>;
  if (q.isError) return <div className="p-6 text-red-400">erro: {(q.error as Error).message}</div>;
  const d = q.data!;

  return (
    <div className="p-6 space-y-4">
      <h2 className="text-xl font-semibold">MPLS overview</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card title="LSPs totais" value={d.total.toLocaleString('pt-BR')} sub={`${d.mplsEnabledDevices} devices habilitados`} />
        <Card title="Tipos" value={Object.entries(d.byType).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'} sub="" />
        <Card title="Por role" value={Object.entries(d.byRole).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'} sub="" />
        <Card title="Atualizado" value={d.perDevice[0]?.lastSeen ? fmtAge(d.perDevice[0].lastSeen) : '—'} sub="último poll" />
      </div>

      <div className="rounded border border-slate-800 bg-slate-900/60 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/60 text-slate-400">
            <tr>
              <th className="text-left px-3 py-2">Device</th>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">mgmtIp</th>
              <th className="text-right px-3 py-2">LSPs</th>
              <th className="text-left px-3 py-2">Último poll</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {d.perDevice.map((row) => (
              <tr key={row.deviceId} className="border-t border-slate-800 hover:bg-slate-800/40">
                <td className="px-3 py-1.5">{row.device?.hostname ?? `device ${row.deviceId}`}</td>
                <td className="px-3 py-1.5 text-slate-400">{row.device?.vendor?.code ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-slate-400">{row.device?.mgmtIp ?? '—'}</td>
                <td className="px-3 py-1.5 text-right font-mono">{row.count.toLocaleString('pt-BR')}</td>
                <td className="px-3 py-1.5 text-slate-500">{fmtAge(row.lastSeen)}</td>
                <td className="px-3 py-1.5 text-right">
                  <a href={`#mpls/${row.deviceId}`}
                     className="text-cyan-400 hover:text-cyan-300 text-xs">abrir →</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        F6 read-only — listagem de LSPs (LDP/BGP/Static/RSVP-TE). Criação/edição de túnel
        MPLS-TE não está habilitada nesta fase.
      </p>
    </div>
  );
}

function MplsDeviceDetail({ deviceId, canWrite }: { deviceId: number; canWrite: boolean }) {
  const qc = useQueryClient();
  const [lspType, setLspType] = useState('');
  const [role, setRole] = useState('');
  const [fec, setFec] = useState('');
  const [skip, setSkip] = useState(0);
  const take = 50;

  const q = useQuery<LspsResp>({
    queryKey: ['mpls', deviceId, lspType, role, fec, skip],
    queryFn: () => {
      const qs = new URLSearchParams({ take: String(take), skip: String(skip) });
      if (lspType) qs.set('lspType', lspType);
      if (role) qs.set('role', role);
      if (fec) qs.set('fec', fec);
      return api.get(`/api/devices/${deviceId}/mpls/lsps?${qs}`);
    },
    refetchInterval: 60_000
  });

  const refresh = useMutation({
    mutationFn: () => api.post(`/api/devices/${deviceId}/mpls/refresh`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mpls'] })
  });

  if (q.isLoading) return <div className="p-6 text-slate-400">carregando LSPs…</div>;
  if (q.isError) return <div className="p-6 text-red-400">erro: {(q.error as Error).message}</div>;
  const d = q.data!;
  const pageEnd = Math.min(d.skip + d.lsps.length, d.total);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <a href="#mpls" className="text-cyan-400 hover:text-cyan-300 text-sm">← overview</a>
        <h2 className="text-xl font-semibold ml-3">{d.device.hostname}</h2>
        <span className="text-slate-500 text-sm font-mono">{d.device.mgmtIp}</span>
        <span className="text-xs px-2 py-0.5 rounded bg-slate-800 ring-1 ring-slate-700">{d.device.vendor.code}</span>
        <span className="text-xs px-2 py-0.5 rounded bg-slate-800 ring-1 ring-slate-700">{d.device.model.code ?? '—'}</span>
        <span className="ml-auto text-xs text-slate-500">{d.total.toLocaleString('pt-BR')} LSPs · {fmtAge(d.lastSeen)}</span>
        {canWrite && (
          <button onClick={() => refresh.mutate()} disabled={refresh.isPending}
                  className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-50">
            {refresh.isPending ? 'sincronizando…' : '↻ Re-sincronizar'}
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Picker label="Tipo" value={lspType} onChange={(v) => { setLspType(v); setSkip(0); }}
                options={['', 'LDP', 'RSVP_TE', 'BGP_LSP', 'BGP_IPV6_LSP', 'STATIC', 'EVPN', 'UNKNOWN']} />
        <Picker label="Role" value={role} onChange={(v) => { setRole(v); setSkip(0); }}
                options={['', 'INGRESS', 'TRANSIT', 'EGRESS', 'UNKNOWN']} />
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-400">FEC contém</span>
          <input className="bg-slate-800 border border-slate-700 rounded px-2 py-1 font-mono text-sm w-40"
                 value={fec} onChange={(e) => { setFec(e.target.value); setSkip(0); }} placeholder="10.255." />
        </label>
      </div>

      <div className="rounded border border-slate-800 bg-slate-900/60 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/60 text-slate-400">
            <tr>
              <th className="text-left px-3 py-2">Tipo</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Action</th>
              <th className="text-left px-3 py-2">FEC</th>
              <th className="text-right px-3 py-2">In Label</th>
              <th className="text-right px-3 py-2">Out Label</th>
              <th className="text-left px-3 py-2">Out interface</th>
              <th className="text-left px-3 py-2">VRF</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {d.lsps.map((l) => (
              <tr key={l.id} className="border-t border-slate-800">
                <td className={`px-3 py-1.5 font-mono text-xs ${TYPE_COLOR[l.lspType] ?? ''}`}>{l.lspType}</td>
                <td className={`px-3 py-1.5 ${ROLE_COLOR[l.role] ?? ''}`}>{l.role}</td>
                <td className="px-3 py-1.5 text-slate-400 font-mono">{l.action ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono">{l.fec}</td>
                <td className="px-3 py-1.5 text-right font-mono">{l.inLabel ?? '—'}</td>
                <td className="px-3 py-1.5 text-right font-mono">{l.outLabel === 3 ? <span title="ImpNull (PHP)" className="text-slate-500">ImpNull</span> : l.outLabel ?? '—'}</td>
                <td className="px-3 py-1.5 text-slate-300 font-mono">{l.outInterface ?? '—'}</td>
                <td className="px-3 py-1.5 text-slate-500">{l.vrfName ?? '—'}</td>
                <td className="px-3 py-1.5 text-slate-400">{l.status ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {d.lsps.length === 0 && (
          <div className="p-4 text-center text-slate-500 text-sm">nenhum LSP encontrado</div>
        )}
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">
          mostrando {d.skip + 1}–{pageEnd} de {d.total.toLocaleString('pt-BR')}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setSkip(Math.max(0, skip - take))}
                  disabled={skip === 0}
                  className="px-3 py-1 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-30">← anterior</button>
          <button onClick={() => setSkip(skip + take)}
                  disabled={pageEnd >= d.total}
                  className="px-3 py-1 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-30">próxima →</button>
        </div>
      </div>
    </div>
  );
}

function Picker({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-slate-400">{label}</span>
      <select className="bg-slate-800 border border-slate-700 rounded px-2 py-1"
              value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o} value={o}>{o === '' ? '— todos —' : o}</option>)}
      </select>
    </label>
  );
}

function Card({ title, value, sub }: { title: string; value: string; sub: string }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/60 p-3">
      <div className="text-xs text-slate-500 uppercase">{title}</div>
      <div className="text-lg font-mono">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
